/**
 * useFileUpload Hook
 * Provides file upload functionality with progress tracking and error handling
 * Automatically processes files after upload
 */

import { useCallback, useState } from 'react';
import {
  type FileUploadRequest,
  type FileUploadResponse,
  processFile,
  uploadFile,
} from '@/api/file-upload-api';

export interface UseFileUploadOptions {
  onSuccess?: (response: FileUploadResponse) => void;
  onError?: (error: Error) => void;
  autoProcess?: boolean; // Default: true - automatically process after upload
}

export interface UseFileUploadReturn {
  upload: (request: FileUploadRequest) => Promise<FileUploadResponse | null>;
  isUploading: boolean;
  isProcessing: boolean;
  error: Error | null;
  progress: number;
  reset: () => void;
}

export function useFileUpload(options?: UseFileUploadOptions): UseFileUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [progress, setProgress] = useState(0);

  const upload = useCallback(
    async (request: FileUploadRequest): Promise<FileUploadResponse | null> => {
      const shouldAutoProcess = options?.autoProcess !== false; // Default to true

      setIsUploading(true);
      setError(null);
      setProgress(0);

      try {
        // Step 1: Upload file
        setProgress(25);
        const uploadResponse = await uploadFile(request);
        setProgress(50);
        setIsUploading(false);

        // Step 2: Automatically process file (if enabled)
        if (shouldAutoProcess) {
          setIsProcessing(true);
          setProgress(60);

          try {
            const processResponse = await processFile(uploadResponse.file.file_id);
            setProgress(100);

            // Merge processing result into upload response
            const combinedResponse: FileUploadResponse = {
              ...uploadResponse,
              processed: processResponse.processed,
              processing_result: processResponse.processing_result,
            };

            setIsProcessing(false);

            if (options?.onSuccess) {
              options.onSuccess(combinedResponse);
            }

            return combinedResponse;
          } catch {
            // Processing failed, but upload succeeded
            setIsProcessing(false);
            setProgress(100);

            // Still call onSuccess with upload response (processed: false)
            if (options?.onSuccess) {
              options.onSuccess(uploadResponse);
            }

            return uploadResponse;
          }
        } else {
          // No auto-processing, just return upload response
          setProgress(100);

          if (options?.onSuccess) {
            options.onSuccess(uploadResponse);
          }

          return uploadResponse;
        }
      } catch (err) {
        const uploadError = err instanceof Error ? err : new Error('Upload failed');
        setError(uploadError);
        setProgress(0);
        setIsUploading(false);
        setIsProcessing(false);

        if (options?.onError) {
          options.onError(uploadError);
        }

        return null;
      }
    },
    [options]
  );

  const reset = useCallback(() => {
    setIsUploading(false);
    setIsProcessing(false);
    setError(null);
    setProgress(0);
  }, []);

  return {
    upload,
    isUploading,
    isProcessing,
    error,
    progress,
    reset,
  };
}
